import { ConfigField, ConfigSlideId, Slide } from '@/eventConfig/types.ts';
import React, {
  ChangeEventHandler,
  CSSProperties,
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { decorate } from '@/form/fields/_pipeline/decorate.ts';
import { normalize } from '@/form/fields/_pipeline/normalize.ts';
import { FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { isBmiSlide } from '@/form/utils';
import { useFieldValidation } from '../_pipeline/useValidateField';
import { useBmiValidation } from '@/util/useBmiValidation';
import useSlideIcons from '@/util/slideIcons';
import { useRevalidateField } from '@/util/useRevalidateField.ts';

interface NumberInputProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const NumberInput: FC<NumberInputProps> = ({ field, slideId, getFieldStatus, onChange, getFieldValue }) => {
  const { showLayer } = useLayer();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { handleUiEvent } = useUiEvent();
  const { isValidBmi } = useBmiValidation(getFieldValue);
  const { revalidateFields } = useRevalidateField(validateField);
  const { infoIcon } = useSlideIcons({} as Slide);

  const fieldStatus = getFieldStatus(field.name);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');
  const isAge = validators.find((v) => v.type === 'age');
  const placeholder = translate(field.placeholder);

  useEffect(() => {
    const e = { target: { value: value } } as React.ChangeEvent<HTMLInputElement>;
    if (isBmiSlide(slideId)) handleChange(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidBmi]);

  const hasInfoLayer = !!field.info;
  const handleInfoClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    handleUiEvent({
      type: 'layer',
      text: field?.info?.text,
      title: field?.info?.title,
    });
  };

  const infoIconStyles: CSSProperties = {
    position: 'unset',
    marginRight: '0.5rem',
    cursor: 'pointer',
  };

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const raw = e.target.value;
    const normalized = normalize(field.datatype, raw);
    const fieldValue = { raw, normalized };

    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
  };

  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!field.decorator) {
      return;
    }
    const value = e.currentTarget.value;
    if (['Enter', 'Backspace', 'Delete'].indexOf(e.key) < 0) {
      const decorated = decorate(field.decorator, value);
      const normalized = normalize(field.datatype, decorated);
      const fieldValue = { raw: decorated, normalized };
      const { hint, isValid } = validateField(field.validator, fieldValue);

      onChange(field.name, { value: fieldValue, hint, isValid, slideId });

      /* @ts-ignore */
      if (isAge && decorated?.length >= 10 && !isValid) {
        showLayer({ ref: '@ageHint' });
      }
    }
    if (field.revalidate) revalidateFields(field.revalidate);
  };

  const hint = fromTemplate(fieldHint || field.hint || '');

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
    hint ? 'has-hint' : null,
  ];

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <label htmlFor={htmlId} className={isRequired ? 'required' : ''} onClick={handleLabelClick}>
          {hasInfoLayer && (
            <span onClick={handleInfoClick}>
              <i className={infoIcon} style={infoIconStyles} />
            </span>
          )}
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>
        <input
          type="text"
          value={value}
          name={field.name}
          placeholder={placeholder}
          id={htmlId}
          pattern="\d*"
          maxLength={15}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
        />
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
